import { rem } from 'polished'
import { useCallback, useContext, useMemo } from 'react'
import * as React from 'react'
import { useIntl } from 'react-intl'
import styled from 'styled-components'

import MyTripsTileShareContent from './MyTripsTileShareContent'

import type { TripPlansSource } from 'analytics/eventDefinitions'
import {
  tripPlansKebabClicked,
  tripPlansTripClicked,
} from 'analytics/eventDefinitions'
import { fireInteractionEvent } from 'api/googleTagManager'
import ClickableLink from 'components/Common/Clickable/ClickableLink'
import Image from 'components/Common/Image'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import IconButton from 'LuxKit/Button/IconButton'
import LineCalendarIcon from 'LuxKit/Icons/line/LineCalendarIcon'
import LineHeartIcon from 'LuxKit/Icons/line/LineHeartIcon'
import LineMapMarkerIcon from 'LuxKit/Icons/line/LineMapMarkerIcon'
import SolidEllipsisVIcon from 'LuxKit/Icons/solid/SolidEllipsisVIcon'
import CaptionTextBlock from 'LuxKit/TextBlocks/CaptionTextBlock'
import BodyText from 'LuxKit/Typography/BodyText'
import { mediaQueryUp } from 'components/utils/breakpoint'
import CSSBreakpoint from 'components/utils/CSSBreakpoint'
import Group from 'components/utils/Group'
import ModalContext from 'contexts/ModalContext'
import { ModalElementContext } from 'contexts/ModalProvider'
import TripPanelActionMenu from 'tripPlanner/components/TripModal/ActionMenus/TripPanelActionMenu'
import { DEFAULT_TRIP_IMAGE } from 'tripPlanner/config'
import type { BasicTrip } from 'tripPlanner/types/common'
import { formatCasualDateRange, getTripURL, getTripPermissions } from 'tripPlanner/utils'
import { getDestinationsLabel } from 'tripPlanner/utils/places'

const Tile = styled(ClickableLink)`
  position: relative;

  display: grid;
  grid-template-rows: 100%;
  grid-template-columns: ${rem(80)} 1fr min-content;
  grid-template-areas: 'image content button';
  width: 100%;
  gap: ${rem(12)};

  ${mediaQueryUp.desktop} {
    width: ${rem(364)};
    grid-template-rows: ${rem(186)} auto;
    grid-template-columns: 100%;
    grid-template-areas: 'image' 'content';

    &.drawer {
      width: ${rem(308)};
      grid-template-rows: ${rem(186)} auto;
    }
  }
`

const TileContent = styled(VerticalSpacer)`
  grid-area: content;
  width: 100%;
  overflow: hidden;

  > * {
    text-overflow: ellipsis;
    overflow: hidden;
  }
`

const TripName = styled(BodyText)`
  margin-top: ${rem(-4)}; // Compensate for whitespace at the top of the text
`

const TripImage = styled.div`
  grid-area: image;
  position: relative;
  border-radius: ${(props) => props.theme.borderRadius.S};
  overflow: hidden;

  aspect-ratio: 1 / 1;

  > * {
    position: absolute;
    left: 0;
    right: 0;
    bottom: 0;
    top: 0;
  }

  ${mediaQueryUp.desktop} {
    aspect-ratio: 364 /186;

    &.drawer {
      aspect-ratio: 308 /186;
    }
  }
`

const ButtonContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;

  ${mediaQueryUp.desktop} {
    position: absolute;
    top: ${rem(12)};
    right: ${rem(12)};
  }
`

interface Props {
  trip: BasicTrip
  source: TripPlansSource
  className?: string
}

function MyTripsItemTile(props: Props) {
  const { trip, source, className } = props
  const intl = useIntl()
  const showModal = useContext(ModalContext)
  const openModal = useCallback(() => {
    showModal(<TripPanelActionMenu trip={trip} />)
  }, [showModal, trip])

  const onShowMenu = useCallback(
    (e: React.MouseEvent<HTMLButtonElement>) => {
      e.preventDefault()
      e.stopPropagation()
      openModal()
      fireInteractionEvent(tripPlansKebabClicked(source))
    },
    [openModal, source],
  )
  const modalElement = useContext(ModalElementContext)
  const onTileClick = useCallback(() => {
    fireInteractionEvent(tripPlansTripClicked(source))
    modalElement?.resolve()
  }, [modalElement, source])

  const image = useMemo((): App.Image => {
    return {
      id: trip.imageId,
      url: trip.imageId ? DEFAULT_TRIP_IMAGE : undefined,
      title: trip.name,
    }
  }, [trip])

  const destinationsGeo = trip.destinationsGeo
  const hasDestination = destinationsGeo && destinationsGeo.length > 0
  const noDestinationsText = intl.formatMessage({
    id: 'JpmnCD_-hSxcXGq6lCzf',
    defaultMessage: 'No destinations set',
    description: 'Placeholder text when a trip has no destinations configured in the trip planner',
  })
  const mobileDestinationsText = hasDestination ?
      getDestinationsLabel(destinationsGeo, 1) :
    noDestinationsText
  const desktopDestinationsText = hasDestination ?
      getDestinationsLabel(destinationsGeo, 2) :
    noDestinationsText
  const numberOfSavedItems = trip.itemCount
  const hasDates = !!(trip.startDate && trip.endDate)
  const { canEdit } = getTripPermissions(trip)
  return (
    <Tile
      onClick={onTileClick}
      to={getTripURL(trip.id)}
      data-test-id={`trip-panel-${trip.id}`}
      className={className}
    >
      <TripImage className={className}>
        <Image image={image} width={366} fit="center" dpr={2} />
      </TripImage>
      <TileContent gap={4}>
        <Group
          direction="horizontal"
          gap={8}
          horizontalAlign="space-between"
          verticalAlign="start"
        >
          <TripName variant="large" wrap="word-wrap" lineClamp={2}>
            {trip.name}
          </TripName>
          <CSSBreakpoint min="desktop">
            <CaptionTextBlock
              startIcon={<LineHeartIcon size="XS" />}
              variant="medium"
              weight="bold"
            >
              {numberOfSavedItems}
            </CaptionTextBlock>
          </CSSBreakpoint>
        </Group>
        <CaptionTextBlock
          startIcon={<LineMapMarkerIcon size="XS" />}
          variant="medium"
          colour={hasDestination ? 'neutral-one' : 'neutral-three'}
        >
          <CSSBreakpoint max="mobile">{mobileDestinationsText}</CSSBreakpoint>
          <CSSBreakpoint min="tablet">{desktopDestinationsText}</CSSBreakpoint>
        </CaptionTextBlock>
        <CaptionTextBlock
          startIcon={<LineCalendarIcon size="XS" />}
          variant="medium"
          colour={hasDates ? 'neutral-one' : 'neutral-three'}
        >
          {hasDates && formatCasualDateRange(trip.startDate!, trip.endDate)}
          {!hasDates &&
            intl.formatMessage({
              id: 'ceY44qOqCePkdoDrY022',
              defaultMessage: 'No dates added',
              description: 'Placeholder when trip has no dates set in the trip planner feature',
            })}
        </CaptionTextBlock>
        <CSSBreakpoint max="tablet">
          <CaptionTextBlock
            startIcon={<LineHeartIcon />}
            variant="medium"
            weight="bold"
          >
            {numberOfSavedItems}
          </CaptionTextBlock>
        </CSSBreakpoint>
        <MyTripsTileShareContent trip={trip} />
      </TileContent>
      {canEdit && (
        <ButtonContainer style={{ gridArea: 'button' }}>
          <IconButton
            onClick={onShowMenu}
            kind="primary"
            variant="ghost"
            shape="circle"
            size="small"
          >
            <SolidEllipsisVIcon />
          </IconButton>
        </ButtonContainer>
      )}
    </Tile>
  )
}

export default MyTripsItemTile
